import { Checkbox, Field, Input, Select } from '@cloudflare/kumo';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormPage, FormSection } from './ui/FormPage';
import { PasswordField } from './ui/PasswordField';
import { User, UserInput } from '../types';
import { useAuth } from '../contexts/AuthContext';

// Inclui SUPERADMIN: antes o enum só tinha ADMIN/USER, então editar um
// SUPERADMIN obrigava a escolher um desses papéis e o rebaixava ao salvar.
const roleSchema = z.enum(['SUPERADMIN', 'ADMIN', 'USER'], { error: 'Selecione um papel' });

const createUserSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('E-mail inválido'),
  senha: z.string()
    .min(6, 'Senha deve ter pelo menos 6 caracteres')
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Deve conter maiúscula, minúscula e número'),
  role: roleSchema,
  ativo: z.boolean(),
});

const updateUserSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: z.string().email('E-mail inválido'),
  // Campo vazio = manter a senha atual (digitar e apagar deixava '' e falhava no min(6))
  senha: z.preprocess(
    (v) => (v === '' ? undefined : v),
    z.string()
      .min(6, 'Senha deve ter pelo menos 6 caracteres')
      .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Deve conter maiúscula, minúscula e número')
      .optional(),
  ),
  role: roleSchema,
  ativo: z.boolean(),
});

type UserFormData = z.infer<typeof createUserSchema> | z.infer<typeof updateUserSchema>;

interface UserFormProps {
  user?: User;
  onSubmit: (data: UserInput) => Promise<void>;
  onCancel: () => void;
}

const ROLE_OPTIONS = [
  { value: 'SUPERADMIN', label: 'Super Administrador' },
  { value: 'USER', label: 'Usuário' },
  { value: 'ADMIN', label: 'Administrador' },
];

export function UserForm({ user, onSubmit, onCancel }: UserFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user: currentUser } = useAuth();
  // O backend não permite alterar o próprio papel/status
  const isEditingSelf = !!user && user.id === currentUser?.id;
  const canAssignSuperAdmin = currentUser?.role === 'SUPERADMIN' || user?.role === 'SUPERADMIN';

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<UserFormData>({
    resolver: zodResolver(user ? updateUserSchema : createUserSchema),
    defaultValues: user
      ? { nome: user.nome, email: user.email, role: user.role as 'SUPERADMIN' | 'ADMIN' | 'USER', ativo: user.ativo }
      : { nome: '', email: '', senha: '', role: 'USER', ativo: true },
  });

  const handleFormSubmit = async (data: UserFormData) => {
    setIsSubmitting(true);
    try {
      const submitData: UserInput = {
        nome: data.nome,
        email: data.email,
        role: data.role,
        ativo: data.ativo,
      };
      if (data.senha) submitData.senha = data.senha;
      await onSubmit(submitData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const roleOptions = ROLE_OPTIONS.filter((r) => canAssignSuperAdmin || r.value !== 'SUPERADMIN');
  const senhaError = (errors as { senha?: { message?: string } }).senha?.message;

  return (
    <FormPage
      backTo="/usuarios"
      backLabel="Membros"
      onBack={onCancel}
      title={user ? 'Editar usuário' : 'Novo usuário'}
      description={user ? 'Atualize os dados e o papel desta pessoa na organização.' : 'Crie uma conta e defina o papel dela na organização.'}
      formId="user-form"
      submitLabel={user ? 'Salvar alterações' : 'Criar usuário'}
      submitting={isSubmitting}
      onCancel={onCancel}
    >
      <form id="user-form" onSubmit={handleSubmit(handleFormSubmit)} noValidate>
        <FormSection title="Dados do usuário">
        <Field label="Nome" error={errors.nome?.message ? { message: errors.nome?.message, match: true } : undefined}>
          <Controller
            name="nome"
            control={control}
            render={({ field }) => (
              <Input onChange={(e) => field.onChange(e.target.value)} aria-label="Nome" value={field.value} disabled={isSubmitting} />
            )}
          />
        </Field>

        <Field label="E-mail" error={errors.email?.message ? { message: errors.email?.message, match: true } : undefined}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <Input onChange={(e) => field.onChange(e.target.value)} aria-label="E-mail" value={field.value} type="email" disabled={isSubmitting} />
            )}
          />
        </Field>

        {/* O `PasswordField` já traz o próprio `FormField`; o erro do schema
            aparece logo abaixo, no mesmo lugar do campo. */}
        <div>
          <Controller
            name="senha"
            control={control}
            render={({ field }) => (
              <PasswordField
                label={user ? 'Nova Senha (deixe em branco para manter)' : 'Senha'}
                value={field.value ?? ''}
                onChange={field.onChange}
                disabled={isSubmitting}
              />
            )}
          />
          {senhaError && (
            <p className="text-kumo-danger" style={{ fontSize: 13, marginTop: 4 }}>
              {senhaError}
            </p>
          )}
        </div>

        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <Field label="Papel" description={isEditingSelf ? 'Você não pode alterar o seu próprio papel' : undefined} error={errors.role?.message ? { message: errors.role?.message, match: true } : undefined}>
              <Select value={(roleOptions.find((o) => o.value === field.value) ?? null)?.value ?? null} items={roleOptions} onValueChange={(value: string | null) => field.onChange(value ?? field.value)} disabled={isSubmitting || isEditingSelf} />
            </Field>
          )}
        />

        <Controller
          name="ativo"
          control={control}
          render={({ field }) => (
            <Checkbox onCheckedChange={(checked: boolean) => field.onChange(checked)} checked={field.value} disabled={isSubmitting || isEditingSelf} label={<>
              Usuário ativo
            </>} />
          )}
        />

        </FormSection>
      </form>
    </FormPage>
  );
}
